import type { AnalyticsEvent, ApiClient } from "@cfg/api-contract";

/** Tiny event queue: view, configure, export, save, share. Flushed in batches, never blocking the UI. */
export class Analytics {
  private queue: AnalyticsEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | undefined;
  private lastConfigure = new Map<string, number>();

  constructor(
    private readonly api: ApiClient | null,
    private readonly enabled: boolean,
    private readonly baseUrl?: string,
  ) {
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", () => this.flush(true));
    }
  }

  track(type: AnalyticsEvent["type"], designId: string, version?: string, props: AnalyticsEvent["props"] = {}) {
    if (!this.enabled || !this.api) return;
    if (type === "configure") {
      // one configure event per design per 10 s is plenty
      const last = this.lastConfigure.get(designId) ?? 0;
      if (Date.now() - last < 10_000) return;
      this.lastConfigure.set(designId, Date.now());
    }
    this.queue.push({ type, designId, version, props, ts: new Date().toISOString() });
    if (this.queue.length >= 20) this.flush();
    else if (!this.timer) this.timer = setTimeout(() => this.flush(), 8000);
  }

  flush(beacon = false) {
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    if (!this.queue.length || !this.api) return;
    const events = this.queue.splice(0, 50);
    if (beacon && this.baseUrl && typeof navigator !== "undefined" && "sendBeacon" in navigator) {
      navigator.sendBeacon(`${this.baseUrl.replace(/\/$/, "")}/events`, new Blob([JSON.stringify({ events })], { type: "application/json" }));
      return;
    }
    this.api.track(events).catch(() => {
      /* analytics must never surface errors */
    });
  }
}
